import axios from "../api/axios";
import { TreatmentPlanStatus } from "../types/treatmentPlan";

/**
 * Patient response from /patients endpoint
 */
export interface Patient {
  id: number;
  externalId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  ownerUserId: number;
  providerFirstName?: string;
  providerLastName?: string;
  providerName: string | null; // Full provider name for sorting/display
  providerAvatarUrl: string | null;
  latestTreatmentPlanId: number | null;
  latestTreatmentPlanName: string | null;
  latestTreatmentPlanStatus?: TreatmentPlanStatus;
  latestTreatmentPlanCreatedAt?: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown; // Index signature for DataTable compatibility
}

export interface GetPatientsParams {
  search?: string;
  providerId?: number[];
  treatmentPlanStatuses?: TreatmentPlanStatus[];
  start?: number;
  limit?: number;
  total?: boolean;
  orderBy?: string;
  order?: 0 | 1; // 0 = Asc, 1 = Desc
}

/**
 * Patient details response from /patients/{id} endpoint
 */
export interface PatientDetails {
  id: number;
  externalUid: string;
  basicInfo: {
    id: number;
    fullName: string;
    dateOfBirth: string;
    gender: string;
    email: string;
    phone: string;
    providerId: number;
  };
  allergies: string[];
  sessions: {
    sessionId: number;
    sessionDate: string;
    sessionType: string;
    providerId?: number;
    providerName: string;
    treatmentPlanId: number;
    treatmentPlanName: string;
  }[];
}

export const patientsService = {
  /**
   * Get patients list
   * @param params Query parameters
   * @returns List of patients with total count
   */
  async getPatients(params: GetPatientsParams): Promise<{
    data: Patient[];
    total: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params.search) {
      queryParams.append("search", params.search);
    }

    if (params.providerId && params.providerId.length > 0) {
      params.providerId.forEach((id) => {
        queryParams.append("providerId", id.toString());
      });
    }

    if (params.treatmentPlanStatuses && params.treatmentPlanStatuses.length > 0) {
      params.treatmentPlanStatuses.forEach((status) => {
        queryParams.append("treatmentPlanStatuses", status.toString());
      });
    }

    if (params.start !== undefined) {
      queryParams.append("start", params.start.toString());
    }

    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }

    if (params.total !== undefined) {
      queryParams.append("total", params.total.toString());
    }

    if (params.orderBy) {
      queryParams.append("order-by", params.orderBy);
    }

    if (params.order !== undefined) {
      queryParams.append("order", params.order.toString());
    }

    const response = await axios.get<Patient[]>(
      `/patients?${queryParams.toString()}`,
    );

    return {
      data: response.data || [],
      total: parseInt(response.headers["x-total-count"] as string) || 0,
    };
  },

  /**
   * Get patient by ID (externalUid)
   * @param id Patient external UID
   * @returns Patient details
   */
  async getPatientById(id: number): Promise<PatientDetails> {
    const response = await axios.get<PatientDetails>(`/patients/${id}`);
    return response.data;
  },
};
